import React, { useMemo, useEffect, useCallback, useState, useRef } from 'react'
import { Trans, useLingui } from '@lingui/react/macro'
import { graphql, useFragment } from 'react-relay'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { captureException } from '@sentry/react'

import { XStack, YStack } from 'tamagui'
import { isAuthenticationError } from '@data/Provider'
import { useMutation } from '@data/useMutation'
import { useFlashMessage } from '@newComponents/DynamicIsland/useFlashMessage'
import { Paragraph } from '@newComponents/Text'
import { Button } from '@newComponents/Button/Button'
import { Link } from '@newComponents/Link'
import { Spinner } from '@newComponents/Spinner'
import { Icon } from '@newComponents/Icon'
import { Card } from '@newComponents/Card'
import { H2, Text } from '@newComponents/Text'
import { TierSelectionCard } from '@newComponents/Event/TierSelectionCard'

import {
  EventAttendContent_event$key,
  EventAttendeeStatus,
} from './__generated__/EventAttendContent_event.graphql'
import { EventAttendContentMutation } from './__generated__/EventAttendContentMutation.graphql'
import { EventAttendContentRemoveAttendanceMutation } from './__generated__/EventAttendContentRemoveAttendanceMutation.graphql'
import { EventAttendContentCreateOrderMutation } from './__generated__/EventAttendContentCreateOrderMutation.graphql'

import { EventAttendContent_query$key } from './__generated__/EventAttendContent_query.graphql'

const hasBackgroundLocation = (
  state: unknown
): state is { backgroundLocation?: unknown } =>
  typeof state === 'object' && state !== null && 'backgroundLocation' in state

const getBackgroundLocation = (
  location: ReturnType<typeof useLocation>
): unknown => {
  const state: unknown = location.state
  return hasBackgroundLocation(state) && state.backgroundLocation
    ? state.backgroundLocation
    : location
}

export const EventAttendContent = ({
  event,
  query,
  ...stackProps
}: {
  event: EventAttendContent_event$key
  query: EventAttendContent_query$key
} & import('tamagui').YStackProps) => {
  const { t } = useLingui()
  const data = useFragment(
    graphql`
      fragment EventAttendContent_event on Event {
        rowId
        id

        name
        prettyUrl

        hasVenue
        hasExternalUrl

        acceptingAttendance

        owner {
          __typename

          ... on User {
            firstName
            lastName
          }

          ... on Guild {
            name
            slugId

            myMembership {
              id
            }
          }
        }

        myAttendance {
          visibleStatus
          paymentStatus
        }

        myTicketOrder {
          id
          status
        }

        eventTicketingTiers(orderBy: [SORT_ORDER_ASC, CREATED_AT_ASC]) {
          totalCount
          edges {
            node {
              id
            }
          }
        }

        ...TierSelectionCard_event
      }
    `,
    event
  )

  const hasTiers = data.eventTicketingTiers.totalCount > 0

  const queryData = useFragment(
    graphql`
      fragment EventAttendContent_query on Query {
        viewer {
          id
          slugId
        }
      }
    `,
    query
  )

  const navigate = useNavigate()
  const location = useLocation()
  const { showFlash } = useFlashMessage()
  const backgroundLocation = useMemo(
    () => getBackgroundLocation(location),
    [location]
  )

  const [searchParams] = useSearchParams()
  const status: EventAttendeeStatus =
    (searchParams.get('status') as EventAttendeeStatus | null) ||
    (data.hasVenue ? 'ATTENDING_IN_PERSON' : 'ATTENDING_VIRTUAL')

  const discountCode = searchParams.get('discount')

  const initialTierSelections = useMemo(() => {
    const tiersParam = searchParams.get('tiers')
    if (!tiersParam) return undefined
    try {
      return JSON.parse(tiersParam) as Record<string, number>
    } catch {
      return undefined
    }
  }, [searchParams])

  const confirmedSearchParam = searchParams.get('confirmed')

  const needsConfirmation = useMemo(() => {
    if (status === 'NOT_ATTENDING') {
      return false
    }

    if (confirmedSearchParam === 'true') {
      return false
    }

    if (data.owner?.__typename === 'Guild') {
      return data.owner.myMembership === null
    }

    return true
  }, [data, confirmedSearchParam, status])

  // Note: Optimistic responses are not used for these attendance mutations because
  // they return only fragment spreads across 7 different fragments (EventEditAttendeesList,
  // EventAttendButton, EventItem, EventAttendContent, EventScreenMobile/Desktop,
  // EventScreenAttendeesDialog). Providing an optimistic response would require
  // duplicating all scalar fields from every fragment, which is fragile and would
  // break silently when any fragment changes. The mutations already navigate away
  // or show loading states, so the UX impact of waiting for the server response
  // is minimal.
  const attendEventMutation = useMutation<EventAttendContentMutation>(graphql`
    mutation EventAttendContentMutation($input: AttendEventInput!) {
      attendEvent(input: $input) {
        event {
          ...EventEditAttendeesList_event
          ...EventAttendButtonNew_event
          ...EventItemNew_event
          ...EventAttendContent_event

          ...EventScreenNewMobile_event
          ...EventScreenNewDesktop_event
          ...EventScreenAttendeesDialogContent_event
        }
      }
    }
  `)

  const removeEventAttendanceMutation =
    useMutation<EventAttendContentRemoveAttendanceMutation>(graphql`
      mutation EventAttendContentRemoveAttendanceMutation(
        $input: RemoveEventAttendanceInput!
      ) {
        removeEventAttendance(input: $input) {
          event {
            ...EventEditAttendeesList_event
            ...EventAttendButtonNew_event
            ...EventItemNew_event
            ...EventAttendContent_event

            ...EventScreenNewMobile_event
            ...EventScreenNewDesktop_event
            ...EventScreenAttendeesDialogContent_event
          }
        }
      }
    `)

  const createOrderMutation =
    useMutation<EventAttendContentCreateOrderMutation>(graphql`
      mutation EventAttendContentCreateOrderMutation(
        $input: CreateTicketOrderInput!
      ) {
        createEventTicketOrder(input: $input) {
          __typename

          ... on CreateEventTicketOrderNoPaymentNeededPayload {
            eventTicketOrder {
              id
              status
            }
          }

          ... on CreateEventTicketOrderPaymentNeededPayload {
            eventTicketOrder {
              id
            }
          }
        }
      }
    `)

  const [isCreatingOrder, setIsCreatingOrder] = useState(false)

  const [isSubmitting, setIsSubmitting] = useState(false)

  // An expired/invalid session cannot complete attendance mutations. Redirect
  // to sign-in with the attend path preserved so the user resumes where they
  // left off — mirrors GuildJoinConfirmationDialog's session handling.
  const redirectToSignIn = useCallback(
    (authRedirectPath: string) => {
      navigate('/sign-in', {
        replace: true,
        state: {
          backgroundLocation,
          authRedirectPath,
        },
      })
    },
    [backgroundLocation, navigate]
  )

  const attendEventAction = useCallback(async () => {
    if (!isSubmitting) {
      try {
        setIsSubmitting(true)

        if (!queryData.viewer) {
          const authRedirectPath = discountCode
            ? `/events/${data.prettyUrl}/attend?status=${status}&confirmed=true&discount=${encodeURIComponent(discountCode)}`
            : `/events/${data.prettyUrl}/attend?status=${status}&confirmed=true`

          if (data.owner?.__typename === 'Guild' && !data.owner.myMembership) {
            navigate(
              `/${data.owner.slugId}/confirm?eventAttendPath=${encodeURIComponent(authRedirectPath)}`,
              {
                state: {
                  backgroundLocation,
                },
              }
            )
            return
          }

          navigate('/sign-up', {
            state: {
              backgroundLocation,
              authRedirectPath,
            },
          })
          return
        }

        // If this is a guild event and user isn't a member, route through guild
        // confirm so they can join (and fill out a membership form if required)
        // before attending. The guild confirm dialog redirects back here via
        // eventAttendPath after the user joins.
        if (data.owner?.__typename === 'Guild' && !data.owner.myMembership) {
          const eventAttendURL = `/events/${data.prettyUrl}/attend?status=${status}&confirmed=true`
          navigate(
            `/${data.owner.slugId}/confirm?eventAttendPath=${encodeURIComponent(eventAttendURL)}`,
            {
              state: {
                backgroundLocation,
              },
            }
          )
          return
        }

        if (status === 'NOT_ATTENDING') {
          await removeEventAttendanceMutation({
            variables: { input: { eventId: data.rowId } },
          })
        } else {
          await attendEventMutation({
            variables: { input: { eventId: data.rowId, status } },
          })
        }

        // For ticketed events, the component will re-render with the TierSelectionCard
        // after the attendance mutation updates myAttendance.status
        if (hasTiers && status !== 'NOT_ATTENDING') {
          return
        }

        const flashMessage =
          status === 'NOT_ATTENDING'
            ? t`You're no longer attending`
            : status.startsWith('WAITING')
              ? t`You're on the waitlist`
              : t`You're attending!`

        showFlash(flashMessage, 'success')

        if (status === 'NOT_ATTENDING') {
          navigate(`/events/${data.prettyUrl}`)
        }
      } catch (err) {
        if (isAuthenticationError(err)) {
          redirectToSignIn(
            discountCode
              ? `/events/${data.prettyUrl}/attend?status=${status}&confirmed=true&discount=${encodeURIComponent(discountCode)}`
              : `/events/${data.prettyUrl}/attend?status=${status}&confirmed=true`
          )
          return
        }

        captureException(err, {
          extra: {
            eventId: data.id,
            eventName: data.name,
            status,
          },
        })
        showFlash(t`Failed to update attendance`, 'error')
      } finally {
        setIsSubmitting(false)
      }
    }
  }, [
    isSubmitting,
    attendEventMutation,
    removeEventAttendanceMutation,
    data,
    navigate,
    backgroundLocation,
    redirectToSignIn,
    showFlash,
    hasTiers,
    status,
    discountCode,
    queryData.viewer,
    t,
  ])

  const name = useMemo(() => {
    if (!data.owner) {
      return null
    }
    if (data.owner.__typename === 'Guild') {
      return data.owner.name
    } else if (data.owner.__typename === 'User') {
      return `${data.owner.firstName} ${data.owner.lastName}`
    }
  }, [data.owner])
  const notificationSettingsHref = queryData.viewer
    ? `/${queryData.viewer.slugId}/settings/notifications`
    : '/settings/notifications'

  const handleTierConfirm = useCallback(
    async (tierSelections: Record<string, number>) => {
      if (isCreatingOrder) return

      const tiersParam = encodeURIComponent(JSON.stringify(tierSelections))
      const eventAttendURL = discountCode
        ? `/events/${data.prettyUrl}/attend?status=${status}&confirmed=true&tiers=${tiersParam}&discount=${encodeURIComponent(discountCode)}`
        : `/events/${data.prettyUrl}/attend?status=${status}&confirmed=true&tiers=${tiersParam}`

      if (data.owner?.__typename === 'Guild' && !data.owner.myMembership) {
        navigate(
          `/${data.owner.slugId}/confirm?eventAttendPath=${encodeURIComponent(eventAttendURL)}`,
          {
            state: {
              backgroundLocation,
            },
          }
        )
        return
      }

      // If not authenticated, redirect to sign-up with tier selections preserved
      if (!queryData.viewer) {
        navigate('/sign-up', {
          state: {
            backgroundLocation,
            authRedirectPath: eventAttendURL,
          },
        })
        return
      }

      // If guild event and not a member, route through guild confirm
      if (data.owner?.__typename === 'Guild' && !data.owner.myMembership) {
        navigate(
          `/${data.owner.slugId}/confirm?eventAttendPath=${encodeURIComponent(eventAttendURL)}`,
          {
            state: {
              backgroundLocation,
            },
          }
        )
        return
      }

      setIsCreatingOrder(true)

      try {
        // If user hasn't attended yet (e.g. just returned from auth/guild
        // join) or previously declined, attend first, then create the order
        if (
          !data.myAttendance ||
          data.myAttendance.visibleStatus === 'NOT_ATTENDING'
        ) {
          await attendEventMutation({
            variables: { input: { eventId: data.rowId, status } },
          })
        }

        // Expand tier quantity map into individual items
        const items = Object.entries(tierSelections).flatMap(([tierId, qty]) =>
          Array.from({ length: qty }, () => ({
            ticketingTierId: tierId,
            attendeeFirstName: null,
            attendeeLastName: null,
            attendeeEmail: null,
          }))
        )

        const response = await createOrderMutation({
          variables: {
            input: {
              eventId: data.id,
              discountCode: discountCode || undefined,
              items,
            },
          },
        })

        const result = response.createEventTicketOrder

        if (
          result.__typename === 'CreateEventTicketOrderNoPaymentNeededPayload'
        ) {
          const orderId = result.eventTicketOrder?.id || ''
          navigate(
            `/events/${data.prettyUrl}/payment/confirm?orderId=${encodeURIComponent(orderId)}`,
            { replace: true }
          )
        } else {
          navigate(`/events/${data.prettyUrl}/payment`, {
            state: {
              backgroundLocation: location,
            },
          })
        }
      } catch (err) {
        if (isAuthenticationError(err)) {
          redirectToSignIn(eventAttendURL)
          return
        }

        showFlash(t`Failed to create ticket order`, 'error')
      } finally {
        setIsCreatingOrder(false)
      }
    },
    [
      data.id,
      data.rowId,
      data.prettyUrl,
      data.owner,
      data.myAttendance,
      discountCode,
      status,
      queryData.viewer,
      attendEventMutation,
      createOrderMutation,
      navigate,
      location,
      backgroundLocation,
      redirectToSignIn,
      showFlash,
      isCreatingOrder,
      t,
    ]
  )

  // Guards to prevent the effect from firing mutations/redirects more than once.
  // Without these, Relay store updates can change callback identities (via data
  // dependency) and re-trigger the effect, stacking timers or duplicate mutations.
  const hasActedRef = useRef(false)
  const hasRedirectedRef = useRef(false)

  // Stable refs for callbacks so the effect doesn't re-trigger when Relay
  // store updates give attendEventAction/handleTierConfirm new identities.
  const attendEventActionRef = useRef(attendEventAction)
  attendEventActionRef.current = attendEventAction
  const handleTierConfirmRef = useRef(handleTierConfirm)
  handleTierConfirmRef.current = handleTierConfirm

  useEffect(() => {
    // Users with pending_approval (including rejected) should not trigger mutations
    if (data.myAttendance?.visibleStatus === 'PENDING_APPROVAL') {
      return
    }

    if (status === 'NOT_ATTENDING') {
      if (
        !data.myAttendance ||
        data.myAttendance.visibleStatus === 'NOT_ATTENDING'
      ) {
        navigate(`/events/${data.prettyUrl}`)
      } else if (data.myAttendance?.paymentStatus === 'SUCCEEDED') {
        showFlash(t`Ticket purchases cannot be cancelled`, 'error')
        navigate(`/events/${data.prettyUrl}`, { replace: true })
      } else if (!hasActedRef.current) {
        hasActedRef.current = true
        void attendEventActionRef.current()
      }
      return
    }

    // A NOT_ATTENDING record (user declined earlier, then changed their mind)
    // must behave like having no attendance: without this, the user falls
    // through every render branch to the redirect spinner and is stuck.
    const hasActiveAttendance =
      !!data.myAttendance && data.myAttendance.visibleStatus !== 'NOT_ATTENDING'

    if (!hasActiveAttendance && !needsConfirmation && !hasActedRef.current) {
      hasActedRef.current = true
      // If returning from auth/guild join with tier selections in URL,
      // go straight to checkout (handleTierConfirm handles attend + order)
      if (hasTiers && initialTierSelections) {
        void handleTierConfirmRef.current(initialTierSelections)
      } else {
        void attendEventActionRef.current()
      }
      return
    }

    // Redirect to event page after attendance/payment completes.
    // Guard prevents stacking multiple 3-second timers on Relay store updates.
    if (hasRedirectedRef.current) return

    const isAttendingOrWaiting = [
      'ATTENDING_IN_PERSON',
      'ATTENDING_VIRTUAL',
      'WAITING_IN_PERSON',
      'WAITING_VIRTUAL',
    ].includes(data.myAttendance?.visibleStatus || '')

    if (
      hasTiers &&
      isAttendingOrWaiting &&
      data.myAttendance?.paymentStatus === 'SUCCEEDED'
    ) {
      hasRedirectedRef.current = true
      const timeoutId = setTimeout(() => {
        navigate(`/events/${data.prettyUrl}`)
      }, 3000)

      return () => {
        clearTimeout(timeoutId)
      }
    }

    // For non-ticketed events, redirect to event page after a brief delay
    if (!hasTiers && isAttendingOrWaiting) {
      hasRedirectedRef.current = true
      const timeoutId = setTimeout(() => {
        navigate(`/events/${data.prettyUrl}`)
      }, 3000)

      return () => {
        clearTimeout(timeoutId)
      }
    }
  }, [
    needsConfirmation,
    data.prettyUrl,
    data.myAttendance,
    hasTiers,
    data.myAttendance?.paymentStatus,
    data.acceptingAttendance,
    discountCode,
    initialTierSelections,
    navigate,
    showFlash,
    status,
    queryData.viewer,
    t,
  ])

  if (
    status === 'NOT_ATTENDING' &&
    data.myAttendance?.visibleStatus === 'NOT_ATTENDING'
  ) {
    return (
      <Card gap='$6' {...stackProps}>
        <YStack gap='$4' alignItems='center'>
          <Paragraph>
            <Trans>You&apos;re no longer attending</Trans>
          </Paragraph>

          <YStack
            padding='$3'
            backgroundColor='$primaryColor'
            borderRadius='$20'
          >
            <Icon size='$14' name='checkmark-filled' color='$colorOnPrimary' />
          </YStack>

          <Paragraph size='$4' color='$colorSubtle'>
            <Trans>We&apos;re redirecting you to the event page now</Trans>
          </Paragraph>
        </YStack>
      </Card>
    )
  }

  // Show tier selection for attending/waiting users who haven't paid yet
  // Waiting users can still buy tickets — the old flow redirected them to /payment too
  if (
    hasTiers &&
    data.myAttendance?.paymentStatus !== 'SUCCEEDED' &&
    [
      'ATTENDING_IN_PERSON',
      'ATTENDING_VIRTUAL',
      'WAITING_IN_PERSON',
      'WAITING_VIRTUAL',
    ].includes(data.myAttendance?.visibleStatus || '')
  ) {
    return (
      <TierSelectionCard
        event={data}
        onConfirm={handleTierConfirm}
        confirmLabel={isCreatingOrder ? t`Creating order...` : undefined}
        initialSelections={initialTierSelections}
        {...stackProps}
      />
    )
  }

  if (data.myAttendance?.visibleStatus === 'PENDING_APPROVAL') {
    return (
      <Card gap='$6' {...stackProps}>
        <YStack gap='$4' alignItems='center'>
          <Paragraph>
            <Trans>Your registration is pending approval</Trans>
          </Paragraph>

          <YStack
            padding='$3'
            backgroundColor='$backgroundAccent'
            borderRadius='$20'
          >
            <Icon size='$14' name='clock' color='$primaryColor' />
          </YStack>

          <Paragraph size='$4' color='$colorSubtle'>
            <Trans>The organizer will review your registration</Trans>
          </Paragraph>

          {data.myTicketOrder?.status === 'REQUIRES_CAPTURE' && (
            <Paragraph size='$4' color='$colorSubtle' textAlign='center'>
              <Trans>
                Your payment is on hold and will only be charged if you&apos;re
                approved
              </Trans>
            </Paragraph>
          )}
        </YStack>
      </Card>
    )
  }

  if (data.myAttendance?.visibleStatus) {
    if (
      ['ATTENDING_IN_PERSON', 'ATTENDING_VIRTUAL'].includes(
        data.myAttendance.visibleStatus
      )
    ) {
      return (
        <Card gap='$6' {...stackProps}>
          <YStack gap='$4' alignItems='center'>
            <Paragraph>
              <Trans>You&apos;re attending the event!</Trans>
            </Paragraph>

            <YStack
              padding='$3'
              backgroundColor='$primaryColor'
              borderRadius='$20'
            >
              <Icon
                size='$14'
                name='checkmark-filled'
                color='$colorOnPrimary'
              />
            </YStack>

            <Paragraph size='$4' color='$colorSubtle'>
              <Trans>We&apos;re redirecting you to the event page now</Trans>
            </Paragraph>
          </YStack>
        </Card>
      )
    }

    if (
      ['WAITING_IN_PERSON', 'WAITING_VIRTUAL'].includes(
        data.myAttendance.visibleStatus
      )
    ) {
      if (data.myAttendance.paymentStatus === 'SUCCEEDED') {
        return (
          <Card gap='$6' {...stackProps}>
            <YStack gap='$4' alignItems='center'>
              <YStack gap='$2' alignItems='center'>
                <Paragraph textAlign='center'>
                  <Trans>You&apos;re waiting for a place to open up</Trans>
                </Paragraph>
                <Paragraph textAlign='center'>
                  <Trans>We&apos;ll email you if you get a spot!</Trans>
                </Paragraph>
              </YStack>

              <Spinner size='$12' color='$primaryColor' />

              <Paragraph size='$4' color='$colorSubtle'>
                <Trans>We&apos;re redirecting you to the event page now</Trans>
              </Paragraph>
            </YStack>
          </Card>
        )
      } else {
        return (
          <Card gap='$6' {...stackProps}>
            <YStack gap='$4' alignItems='center'>
              <YStack gap='$2' alignItems='center'>
                <Paragraph textAlign='center'>
                  <Trans>
                    Looks like there aren&apos;t any spots available at this
                    time
                  </Trans>
                </Paragraph>
                <Paragraph textAlign='center'>
                  <Trans>For now, we&apos;ve recorded your interest</Trans>
                </Paragraph>
                <Paragraph textAlign='center'>
                  <Trans>Check back later if any become available!</Trans>
                </Paragraph>
              </YStack>

              <Spinner size='$12' color='$primaryColor' />

              <Paragraph size='$4' color='$colorSubtle'>
                <Trans>We&apos;re redirecting you to the event page now</Trans>
              </Paragraph>
            </YStack>
          </Card>
        )
      }
    }
  }

  // Show tier selection for ticketed events even before attending —
  // lets unauthenticated users browse tiers; auth/guild handled on confirm.
  // A NOT_ATTENDING record counts as "not attending yet": users who declined
  // and changed their mind must reach checkout, not fall through to the
  // redirect spinner below (which never redirects for them).
  // Skip when isCreatingOrder (auto-proceeding after auth/guild join).
  if (
    hasTiers &&
    (!data.myAttendance ||
      data.myAttendance.visibleStatus === 'NOT_ATTENDING') &&
    !isCreatingOrder
  ) {
    return (
      <TierSelectionCard
        event={data}
        onConfirm={handleTierConfirm}
        initialSelections={initialTierSelections}
        {...stackProps}
      />
    )
  }

  if (!needsConfirmation || isSubmitting || isCreatingOrder) {
    return (
      <Card gap='$6' {...stackProps}>
        <YStack gap='$4' alignItems='center'>
          <Spinner size='$12' color='$primaryColor' />

          <Paragraph size='$4' color='$colorSubtle'>
            <Trans>You&apos;ll be redirected automatically</Trans>
          </Paragraph>
        </YStack>
      </Card>
    )
  }

  return (
    <Card gap='$6' {...stackProps}>
      <YStack gap='$1'>
        <H2>
          <Trans>Almost there...</Trans>
        </H2>
        <Text size='$5'>
          {hasTiers
            ? t`Confirm your attendance for ${data.name} to select tickets`
            : data.owner?.__typename === 'Guild' && data.owner.myMembership
              ? t`We're confirming your attendance for ${data.name}`
              : t`We need your consent to share information with ${name}`}
        </Text>
      </YStack>

      <YStack gap='$6' $gtXs={{ display: 'none' }}>
        <YStack gap='$2' flex={1} alignItems='center'>
          <YStack justifyContent='center'>
            <Button onPress={attendEventAction}>
              <Button.Text>
                {hasTiers ? t`Confirm Attendance` : t`Sounds good`}
              </Button.Text>
            </Button>
          </YStack>

          {(data.owner?.__typename === 'Guild' && !data.owner.myMembership) ||
          data.owner?.__typename !== 'Guild' ? (
            <>
              <Paragraph
                size='$4'
                textAlign='center'
                color='$colorMuted'
                maxWidth={348}
              >
                <Trans>
                  By attending the event, you agree to share your information
                  with {name}.
                </Trans>
              </Paragraph>

              <Paragraph
                size='$4'
                textAlign='center'
                color='$colorMuted'
                maxWidth={348}
              >
                <Trans>
                  They may contact you about this event and other community
                  initiatives.
                </Trans>
              </Paragraph>

              <Paragraph
                size='$4'
                textAlign='center'
                color='$colorMuted'
                maxWidth={348}
              >
                <Trans>
                  Tune your notifications in your{' '}
                  <Link
                    color='$primaryColor'
                    size='$4'
                    href={notificationSettingsHref}
                    target='_blank'
                  >
                    Settings
                  </Link>
                  .
                </Trans>
              </Paragraph>
            </>
          ) : null}
        </YStack>
      </YStack>

      <XStack gap='$6' display='none' $gtXs={{ display: 'flex' }}>
        <YStack gap='$2' flex={1} alignItems='flex-end'>
          {(data.owner?.__typename === 'Guild' && !data.owner.myMembership) ||
          data.owner?.__typename !== 'Guild' ? (
            <>
              <Paragraph
                size='$4'
                textAlign='right'
                color='$colorMuted'
                maxWidth={348}
              >
                <Trans>
                  By attending the event, you agree to share your information
                  with {name}.
                </Trans>
              </Paragraph>

              <Paragraph
                size='$4'
                textAlign='right'
                color='$colorMuted'
                maxWidth={348}
              >
                <Trans>
                  They may contact you about this event and other community
                  initiatives.
                </Trans>
              </Paragraph>

              <Paragraph
                size='$4'
                textAlign='center'
                color='$colorMuted'
                maxWidth={348}
              >
                <Trans>
                  You can tune your notifications in your{' '}
                  <Link
                    color='$primaryColor'
                    size='$4'
                    href={notificationSettingsHref}
                    target='_blank'
                  >
                    Settings
                  </Link>
                  .
                </Trans>
              </Paragraph>
            </>
          ) : null}
        </YStack>

        <YStack justifyContent='center'>
          <Button onPress={attendEventAction}>
            <Button.Text>
              {hasTiers ? t`Confirm Attendance` : t`Sounds good`}
            </Button.Text>
          </Button>
        </YStack>
      </XStack>
    </Card>
  )
}
